import React, { useEffect, useRef, useState } from 'react';

const PDFJS_VERSION = '4.10.38';
const PDFJS_CDN = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${PDFJS_VERSION}`;

let pdfJsPromise;

function loadPdfJs() {
  if (!pdfJsPromise) {
    pdfJsPromise = import(/* webpackIgnore: true */ `${PDFJS_CDN}/build/pdf.mjs`).then((pdfjs) => {
      pdfjs.GlobalWorkerOptions.workerSrc = `${PDFJS_CDN}/build/pdf.worker.min.mjs`;
      return pdfjs;
    });
  }
  return pdfJsPromise;
}

function isRenderingCancelledError(err) {
  const message = err?.message || String(err || '');
  return /cancel/i.test(message);
}

function TermsPdfPage({ pdf, pageNumber, width, zoom }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    let renderTask = null;

    (async () => {
      try {
        const page = await pdf.getPage(pageNumber);
        if (cancelled || !canvasRef.current || width <= 0) return;

        const baseViewport = page.getViewport({ scale: 1 });
        const scale = (width / baseViewport.width) * zoom;
        const viewport = page.getViewport({ scale });

        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        canvas.width = Math.floor(viewport.width);
        canvas.height = Math.floor(viewport.height);

        renderTask = page.render({ canvasContext: ctx, viewport });
        await renderTask.promise;
      } catch (err) {
        if (!cancelled && !isRenderingCancelledError(err)) {
          console.error(`Failed to render terms PDF page ${pageNumber}`, err);
        }
      }
    })();

    return () => {
      cancelled = true;
      if (renderTask) {
        renderTask.cancel();
      }
    };
  }, [pdf, pageNumber, width, zoom]);

  return (
    <div className="view-quote-terms-page">
      <canvas ref={canvasRef} className="view-quote-terms-page-canvas" aria-hidden />
    </div>
  );
}

/**
 * Renders terms PDF pages as canvas images so only the outer scroll container
 * scrolls — no embedded PDF viewer scrollbar on the side.
 */
export function ViewQuoteTermsPdf({ url, title, zoom = 1, onPageCount }) {
  const containerRef = useRef(null);
  const onPageCountRef = useRef(onPageCount);
  const [pdf, setPdf] = useState(null);
  const [pageCount, setPageCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    onPageCountRef.current = onPageCount;
  }, [onPageCount]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;

    let frameId = null;

    const updateWidth = () => {
      if (frameId) cancelAnimationFrame(frameId);
      frameId = requestAnimationFrame(() => {
        const next = el.clientWidth || 920;
        setWidth((prev) => (Math.abs(prev - next) > 4 ? next : prev || next));
      });
    };

    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    observer.observe(el);

    return () => {
      if (frameId) cancelAnimationFrame(frameId);
      observer.disconnect();
    };
  }, [loading, pdf, pageCount]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setPdf(null);
    setPageCount(0);
    setWidth(0);
    onPageCountRef.current?.(0);

    loadPdfJs()
      .then((pdfjs) => pdfjs.getDocument(url).promise)
      .then((doc) => {
        if (cancelled) return;
        setPdf(doc);
        setPageCount(doc.numPages);
        onPageCountRef.current?.(doc.numPages);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err);
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [url]);

  if (loading) {
    return (
      <div className="view-quote-terms-loading py-16 text-center text-sm text-gray-600">
        Loading…
      </div>
    );
  }

  if (error || !pdf) {
    return (
      <div className="view-quote-terms-error py-8 text-center text-sm text-red-600">
        {title ? `${title}: ` : ''}Could not load document.
      </div>
    );
  }

  return (
    <div ref={containerRef} className="view-quote-terms-pages" aria-label={title}>
      {width > 0
        ? Array.from({ length: pageCount }, (_, index) => (
            <TermsPdfPage
              key={`terms-page-${index + 1}`}
              pdf={pdf}
              pageNumber={index + 1}
              width={width}
              zoom={zoom}
            />
          ))
        : null}
    </div>
  );
}
